import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { remitosApi, notasEnvioApi } from '../../api/remitos'
import EstadoVigenteBadge from '../../components/EstadoVigenteBadge'
import { fmtFecha } from '../../utils/fecha'
import { colors, font, text } from '../../design/tokens'
import { ESTADO_VIGENTE_OPCIONES } from '../../design/estadosVigentes'
import { Card, Buscador, ChipsScroll, Cargando, ErrorMsg, Vacio, BotonCargarMas, DocTag, Titulo } from './comunes'

// Listado móvil de logística: la misma lista unificada T90 + NE de
// /logistica/remitos (endpoints lean), filtrada client-side.

const POR_PAGINA = 30

export default function MovilLogistica() {
  const navigate = useNavigate()
  const [remitos, setRemitos] = useState([])
  const [notasEnvio, setNotasEnvio] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [q, setQ] = useState('')
  const [estados, setEstados] = useState([])
  const [visibles, setVisibles] = useState(POR_PAGINA)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const [rRes, neRes] = await Promise.all([
        remitosApi.listar({ logistica: 1, per_page: 500, lean: 1 }),
        notasEnvioApi.listar({ per_page: 1000, lean: 1 }),
      ])
      setRemitos(rRes.data.data || [])
      setNotasEnvio(neRes.data.data || [])
      setError(null)
    } catch {
      setError('No se pudieron cargar los documentos de logística.')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  // Lista unificada: T90 directo, T80 a través de sus NEs (mismo criterio que el desktop)
  const items = useMemo(() => {
    const neByRemito = {}
    for (const ne of notasEnvio) (neByRemito[ne.remito_id] ||= []).push(ne)
    const out = []
    for (const r of remitos) {
      if (r.transporte === 80) (neByRemito[r.id] || []).forEach((ne) => out.push({ key: `ne-${ne.id}`, tipo: 'ne', ne, remito: r }))
      else out.push({ key: `r-${r.id}`, tipo: 'remito', remito: r })
    }
    return out
  }, [remitos, notasEnvio])

  const filtrados = useMemo(() => {
    const term = q.trim().toLowerCase()
    return items.filter((i) => {
      const estado = i.tipo === 'ne' ? i.ne.estado_vigente?.codigo : i.remito.estado_vigente?.codigo
      if (estados.length > 0 && !estados.includes(estado)) return false
      if (!term) return true
      const campos = i.tipo === 'ne'
        ? [i.ne.numero_ne, i.ne.remito?.numero_presea, i.ne.remito?.cliente?.nombre, i.ne.destinatario?.nombre]
        : [i.remito.numero_presea, i.remito.cliente?.nombre]
      return campos.some((c) => (c || '').toLowerCase().includes(term))
    })
  }, [items, q, estados])

  const toggleEstado = (v) => {
    setEstados((prev) => (prev.includes(v) ? prev.filter((e) => e !== v) : [...prev, v]))
    setVisibles(POR_PAGINA)
  }

  return (
    <div>
      <Titulo>Logística</Titulo>
      <Buscador value={q} onChange={(v) => { setQ(v); setVisibles(POR_PAGINA) }} placeholder="Buscar remito, NE, cliente o destinatario…" />
      <ChipsScroll options={ESTADO_VIGENTE_OPCIONES} selected={estados} onToggle={toggleEstado} />

      {loading ? <Cargando /> : error ? <ErrorMsg>{error}</ErrorMsg> : filtrados.length === 0 ? (
        <Vacio>No hay documentos que coincidan.</Vacio>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          <div style={{ fontSize: text.sec, color: colors.textMeta, padding: '0 2px' }}>
            {filtrados.length} documento{filtrados.length === 1 ? '' : 's'}
          </div>
          {filtrados.slice(0, visibles).map((i) => {
            const esNe = i.tipo === 'ne'
            const numero = esNe ? i.ne.numero_ne : i.remito.numero_presea
            const nombre = esNe ? (i.ne.destinatario?.nombre || i.ne.remito?.cliente?.nombre) : i.remito.cliente?.nombre
            const sub = esNe ? i.ne.remito?.cliente?.nombre : null
            const zona = esNe ? (i.ne.zona?.nombre || i.ne.remito?.zona?.nombre) : i.remito.zona?.nombre
            const fecha = esNe ? i.ne.remito?.fecha_emision : i.remito.fecha_emision
            const estadoVig = esNe ? i.ne.estado_vigente : i.remito.estado_vigente
            const to = esNe ? `/m/logistica/ne/${i.ne.id}` : `/m/logistica/remito/${i.remito.id}`
            return (
              <Card key={i.key} onClick={() => navigate(to)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <DocTag tipo={i.tipo === 'ne' ? 'ne' : 'remito'} numero={numero} />
                  <span style={{ flex: 1 }} />
                  <EstadoVigenteBadge estadoVigente={estadoVig} />
                </div>
                <div style={{ fontFamily: font.sans, fontWeight: 600, fontSize: 14.5, color: colors.text, margin: '5px 0 3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nombre || '—'}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', minWidth: 0 }}>
                  {sub && sub !== nombre && (
                    <span style={{ fontSize: text.sec, color: colors.textMeta, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</span>
                  )}
                  <span style={{ flex: 1 }} />
                  {zona && <span style={zonaPill}>{zona}</span>}
                  <span style={{ fontSize: text.sec, color: colors.textMeta, fontFamily: font.mono, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                    {fmtFecha(fecha)}
                  </span>
                </div>
              </Card>
            )
          })}
          {filtrados.length > visibles && (
            <BotonCargarMas onClick={() => setVisibles((v) => v + POR_PAGINA)} cargando={false} />
          )}
        </div>
      )}
    </div>
  )
}

const zonaPill = {
  fontSize: 11, color: colors.zonaText, background: colors.zonaBg, padding: '1px 7px',
  borderRadius: 8, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flexShrink: 0,
}
